import type { BookingMessages } from "@/i18n/types";
import type { OnlineBookingSameDayCutoffSettings, PublicBookingConfig } from "@/types";
import { todayLocal } from "@/lib/dates";

function parseCutoffTime(value: string): { hours: number; minutes: number } | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

export function isSameDayArrivalBlockedToday(
  config: PublicBookingConfig | null | undefined,
  now: Date = new Date(),
): boolean {
  const cutoff = config?.same_day_cutoff;
  if (!cutoff?.enabled) return false;
  const parsed = parseCutoffTime(cutoff.cutoff_time);
  if (!parsed) return false;
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const cutoffMinutes = parsed.hours * 60 + parsed.minutes;
  return currentMinutes >= cutoffMinutes;
}

export function earliestBookableArrivalDate(
  config: PublicBookingConfig | null | undefined,
  now: Date = new Date(),
): string {
  const today = todayLocal();
  if (!isSameDayArrivalBlockedToday(config, now)) return today;
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  const y = tomorrow.getFullYear();
  const m = String(tomorrow.getMonth() + 1).padStart(2, "0");
  const d = String(tomorrow.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function sameDayCutoffErrorMessage(
  cutoff: OnlineBookingSameDayCutoffSettings | undefined,
  messages: Pick<BookingMessages["validation"], "sameDayCutoffBlocked">,
): string | null {
  if (!cutoff?.enabled) return null;
  return messages.sameDayCutoffBlocked.replace("{time}", cutoff.cutoff_time);
}

export function validateSameDayCutoff(
  arrival: string,
  config: PublicBookingConfig | null | undefined,
  messages: Pick<BookingMessages["validation"], "sameDayCutoffBlocked">,
  now: Date = new Date(),
): string | null {
  if (!config?.same_day_cutoff?.enabled) return null;
  if (arrival !== todayLocal()) return null;
  if (!isSameDayArrivalBlockedToday(config, now)) return null;
  return sameDayCutoffErrorMessage(config.same_day_cutoff, messages);
}
